import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Paper, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import DsokCustomerDetailLayout from "../components/DsokCustomerDetailLayout";
import { useDsokCustomerDetail } from "../data/useDsokCustomerDetail";
import { getSmartkioskInitialValues } from "app/pages/customers/dsok/utils/getSmartkioskInitialValues";
import { DSOK_CUSTOMER_FIELD_SCHEMA } from "app/pages/customers/dsok/smartkioskFields/SmartkioskCustomerFields";
import { SETTINGS_FIELD_SCHEMA } from "app/pages/customers/dsok/smartkioskFields/SmartkioskSettingsFields";
import TranslationsTopBar from "../translations/TranslationsTopBar";
import TranslationsTree from "../translations/TranslationsTree";
import TranslationsEditor from "../translations/TranslationsEditor";
import {
  useTranslationsState,
  useFilteredTreeAndSelection,
} from "../translations/useTranslationsState";
import { countMissingForKey } from "../translations/translationsHelpers";

const getInitialTranslations = (customer) => {
  if (!customer) return {};
  const initial = getSmartkioskInitialValues(customer);
  const fromSettings =
    initial?.settings?.translations ??
    customer?.dsokSettings?.settings?.translations;
  if (fromSettings && typeof fromSettings === "object") {
    return JSON.parse(JSON.stringify(fromSettings));
  }
  return {};
};

const DSOK_TRANSLATIONS_FILENAME = "dsokTranslations.json";

const DsokCustomerTranslationsPage = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef(null);
  const {
    customer,
    setDirty,
    resetDirty,
    registerSectionUpdatePayload,
    unregisterSectionUpdatePayload,
  } = useDsokCustomerDetail();
  const translationsPayloadRef = React.useRef(null);
  translationsPayloadRef.current = null; // set below after translationsJson is available
  const initialTranslations = useMemo(
    () => getInitialTranslations(customer),
    [customer]
  );

  const [search, setSearch] = useState("");
  const [missingOnly, setMissingOnly] = useState(false);
  const [modulePrefix, setModulePrefix] = useState("");
  const [showOnlyChanged, setShowOnlyChanged] = useState(false);
  const [selection, setSelection] = useState({
    selectedKeyPath: null,
    selectedGroupKeyPath: null,
  });
  const { selectedKeyPath, selectedGroupKeyPath } = selection;
  const initialSelectionSetRef = React.useRef(false);
  const userHasSelectedRef = React.useRef(false);

  const handleDirtyChange = useCallback(
    (isDirty) => {
      if (isDirty) setDirty(true);
      else resetDirty();
    },
    [setDirty, resetDirty]
  );

  const {
    translationsJson,
    setTranslationsJson,
    originalTranslationsJson,
    languages,
    updateValue,
    getMissingCount,
  } = useTranslationsState(initialTranslations, handleDirtyChange);

  translationsPayloadRef.current = translationsJson;

  const handleExport = useCallback(() => {
    const json = JSON.stringify(translationsJson, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = DSOK_TRANSLATIONS_FILENAME;
    a.click();
    URL.revokeObjectURL(url);
  }, [translationsJson]);

  const handleImportFileChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const parsed = JSON.parse(reader.result ?? "{}");
          if (parsed == null || typeof parsed !== "object" || Array.isArray(parsed)) {
            enqueueSnackbar(t("dsokCustomerDetail.translationsUI.importError"), {
              variant: "error",
            });
            return;
          }
          setTranslationsJson(JSON.parse(JSON.stringify(parsed)));
          enqueueSnackbar(t("dsokCustomerDetail.translationsUI.importSuccess"), {
            variant: "success",
          });
        } catch {
          enqueueSnackbar(t("dsokCustomerDetail.translationsUI.importError"), {
            variant: "error",
          });
        }
        e.target.value = "";
      };
      reader.readAsText(file, "UTF-8");
    },
    [setTranslationsJson, enqueueSnackbar, t]
  );

  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  useEffect(() => {
    registerSectionUpdatePayload("translations", () => translationsPayloadRef.current);
    return () => unregisterSectionUpdatePayload("translations");
  }, [registerSectionUpdatePayload, unregisterSectionUpdatePayload]);

  const {
    treeNodes,
    filteredKeyPaths,
    leafKeyPathsUnderGroup,
    hasFilters,
  } = useFilteredTreeAndSelection({
    translationsJson,
    originalTranslationsJson,
    search,
    missingOnly,
    modulePrefix,
    showOnlyChanged,
  });

  const leafKeyPathsInGroup = useMemo(
    () =>
      selectedGroupKeyPath != null
        ? leafKeyPathsUnderGroup(selectedGroupKeyPath)
        : [],
    [selectedGroupKeyPath, leafKeyPathsUnderGroup]
  );

  const getMissingCountForTree = useCallback(
    (keyPath) => {
      const leavesUnder = leafKeyPathsUnderGroup(keyPath);
      if (leavesUnder.length > 0) {
        return leavesUnder.filter(
          (kp) => countMissingForKey(translationsJson, kp, languages) > 0
        ).length;
      }
      return getMissingCount(keyPath);
    },
    [translationsJson, languages, getMissingCount, leafKeyPathsUnderGroup]
  );

  useEffect(() => {
    if (customer?.id != null) {
      setSelection({ selectedKeyPath: null, selectedGroupKeyPath: null });
      initialSelectionSetRef.current = false;
      userHasSelectedRef.current = false;
    }
  }, [customer?.id]);

  const firstVisibleGroup = useMemo(() => {
    const first = treeNodes?.[0];
    if (first && !first.isLeaf) return first.keyPath;
    const firstLeaf = filteredKeyPaths[0];
    return firstLeaf || null;
  }, [treeNodes, filteredKeyPaths]);

  useEffect(() => {
    if (userHasSelectedRef.current) return;
    if (
      !initialSelectionSetRef.current &&
      firstVisibleGroup != null &&
      treeNodes?.length > 0
    ) {
      initialSelectionSetRef.current = true;
      const node = treeNodes.find(
        (n) => n.keyPath === firstVisibleGroup || n.id === firstVisibleGroup
      );
      if (node?.isLeaf) {
        setSelection({ selectedKeyPath: firstVisibleGroup, selectedGroupKeyPath: null });
      } else {
        setSelection({ selectedKeyPath: null, selectedGroupKeyPath: firstVisibleGroup });
      }
    }
  }, [firstVisibleGroup, treeNodes]);

  useEffect(() => {
    const keyStillVisible =
      selectedKeyPath == null || filteredKeyPaths.includes(selectedKeyPath);
    const groupStillVisible =
      selectedGroupKeyPath == null ||
      filteredKeyPaths.some(
        (kp) => kp === selectedGroupKeyPath || kp.startsWith(`${selectedGroupKeyPath}.`)
      );
    if (!keyStillVisible && selectedKeyPath != null) {
      setSelection((prev) => ({ ...prev, selectedKeyPath: null }));
    }
    if (!groupStillVisible && selectedGroupKeyPath != null) {
      setSelection((prev) => ({ ...prev, selectedGroupKeyPath: null }));
    }
  }, [filteredKeyPaths, selectedKeyPath, selectedGroupKeyPath]);

  const handleSelectLeaf = useCallback((keyPath) => {
    userHasSelectedRef.current = true;
    setSelection({ selectedKeyPath: keyPath, selectedGroupKeyPath: null });
  }, []);

  const handleSelectGroup = useCallback((groupKeyPath) => {
    userHasSelectedRef.current = true;
    setSelection({ selectedKeyPath: null, selectedGroupKeyPath: groupKeyPath });
  }, []);

  const formInitialValues = useMemo(
    () =>
      customer
        ? getSmartkioskInitialValues(customer) ?? {
            customer: { ...DSOK_CUSTOMER_FIELD_SCHEMA },
            settings: { ...SETTINGS_FIELD_SCHEMA },
          }
        : { customer: { ...DSOK_CUSTOMER_FIELD_SCHEMA }, settings: { ...SETTINGS_FIELD_SCHEMA } },
    [customer]
  );

  return (
    <DsokCustomerDetailLayout
      lastBreadcrumbLabel={t("dsokCustomerDetail.sections.translations.title")}
    >
      <Paper
        variant="outlined"
        sx={{
          bgcolor: "background.paper",
          borderRadius: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 480,
          // Fixed height so page doesn't scroll; only tree column scrolls → editor stays fixed
          height: "calc(100vh - 200px)",
          maxHeight: 900,
        }}
      >
        <input
          type="file"
          ref={fileInputRef}
          accept=".json,application/json"
          style={{ display: "none" }}
          onChange={handleImportFileChange}
        />
        <TranslationsTopBar
          search={search}
          onSearchChange={setSearch}
          missingOnly={missingOnly}
          onMissingOnlyChange={setMissingOnly}
          modulePrefix={modulePrefix}
          onModulePrefixChange={setModulePrefix}
          showOnlyChanged={showOnlyChanged}
          onShowOnlyChangedChange={setShowOnlyChanged}
          onImportClick={handleImportClick}
          onExportClick={handleExport}
        />
        <Alert severity="info" sx={{ mx: 1.5, mt: 1, flexShrink: 0 }}>
          {t("dsokCustomerDetail.translationsUI.exportHelp")}
        </Alert>
        <Alert severity="info" sx={{ mx: 1.5, mt: 0.5, flexShrink: 0 }}>
          {t("dsokCustomerDetail.translationsUI.importHelp")}
        </Alert>
        <Box
          sx={{
            display: "flex",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              width: 280,
              minWidth: 280,
              borderRight: 1,
              borderColor: "divider",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ px: 2, py: 1, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}
            >
              {t("dsokCustomerDetail.translationsUI.treeTitle")}
            </Typography>
            <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
              <TranslationsTree
                treeNodes={treeNodes}
                selectedKeyPath={selectedKeyPath}
                selectedGroupKeyPath={selectedGroupKeyPath}
                onSelectLeaf={handleSelectLeaf}
                onSelectGroup={handleSelectGroup}
                getMissingCount={getMissingCountForTree}
                filterApplied={hasFilters}
              />
            </Box>
          </Box>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              minWidth: 0,
            }}
          >
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ px: 2, py: 1, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}
            >
              {t("dsokCustomerDetail.translationsUI.editorTitle")}
            </Typography>
            <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
              <TranslationsEditor
                selectedKeyPath={selectedKeyPath}
                selectedGroupKeyPath={selectedGroupKeyPath}
                leafKeyPathsInGroup={leafKeyPathsInGroup}
                translationsJson={translationsJson}
                languages={languages}
                onChange={updateValue}
                getMissingCount={getMissingCount}
              />
            </Box>
          </Box>
        </Box>
      </Paper>
    </DsokCustomerDetailLayout>
  );
};

export default DsokCustomerTranslationsPage;
